import { Box, Typography } from '@mui/material';
import { Stethoscope, UserRound } from 'lucide-react';
import { useIntl } from 'react-intl';
import type {
    ClinicalPeopleList,
    PatientSummaryDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import { personLabel, type Assignee, type ClinicalLists } from '../model';
import AssigneeAvatar from './AssigneeAvatar';
import { SECONDARY_LINE_SX } from './PatientIdentity';

type PatientCareProps = {
    patient: Pick<
        PatientSummaryDto,
        'siteId' | 'radiationOncologistId' | 'plannerId'
    >;
    lists: ClinicalLists;
    people: ClinicalPeopleList | undefined;
    /** Who the patient is assigned to, when anybody is and it is shown here. */
    assignee?: Assignee;
};

/**
 * A patient's site, radiation oncologist and planner as they are shown. No
 * site reads "No site", an unchosen person "Not chosen", a site or person
 * "…" until the lists have loaded, and an id no listed site or user has
 * "Unknown site" or "Unknown user". A retired site is still named.
 */
export function usePatientCare(
    patient: PatientCareProps['patient'],
    lists: ClinicalLists,
    people: ClinicalPeopleList | undefined
) {
    const intl = useIntl();
    const site = lists.sites?.find((each) => each.id === patient.siteId);
    const unnamedSite = (): string => {
        if (!patient.siteId) {
            return intl.formatMessage({ id: 'patientList.care.noSite' });
        }
        return lists.sites === undefined
            ? '…'
            : intl.formatMessage({ id: 'patientList.care.unknownSite' });
    };
    const nameOf = (id: string | null | undefined) =>
        id
            ? personLabel(intl, people, id)
            : intl.formatMessage({ id: 'patientList.notChosen' });
    return {
        siteName: site?.name ?? unnamedSite(),
        siteColor: site?.color,
        oncologist: nameOf(patient.radiationOncologistId),
        planner: nameOf(patient.plannerId),
    };
}

/** A site's name after a square in the site's colour; grey when it has none. */
export function SiteName({
    name,
    color,
}: {
    name: string;
    color: string | null | undefined;
}) {
    return (
        <Box
            component="span"
            sx={{
                ...SECONDARY_LINE_SX,
                color: 'text.primary',
                fontSize: 13,
            }}
        >
            <Box
                component="span"
                aria-hidden
                sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '2px',
                    flexShrink: 0,
                    bgcolor: color ?? 'text.disabled',
                }}
            />
            <Typography component="span" noWrap fontSize="inherit">
                {name}
            </Typography>
        </Box>
    );
}

/** Where and by whom a patient is treated, one line each, and any assignee. */
export default function PatientCare({
    patient,
    lists,
    people,
    assignee,
}: PatientCareProps) {
    const intl = useIntl();
    const care = usePatientCare(patient, lists, people);

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: '3px',
                minWidth: 0,
            }}
        >
            <SiteName name={care.siteName} color={care.siteColor} />
            <Box sx={SECONDARY_LINE_SX}>
                <Stethoscope
                    size={12}
                    aria-label={intl.formatMessage({
                        id: 'patientList.radiationOncologist',
                    })}
                />
                <Typography component="span" noWrap fontSize="inherit">
                    {care.oncologist}
                </Typography>
            </Box>
            <Box sx={SECONDARY_LINE_SX}>
                <UserRound
                    size={12}
                    aria-label={intl.formatMessage({
                        id: 'patientList.planner',
                    })}
                />
                <Typography component="span" noWrap fontSize="inherit">
                    {care.planner}
                </Typography>
            </Box>
            {assignee && (
                <Box sx={SECONDARY_LINE_SX}>
                    <AssigneeAvatar assignee={assignee} />
                    <Typography component="span" noWrap fontSize="inherit">
                        {assignee.name}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
